import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { getAllAgents } from '../../api/userApi';
import { getAllCommissionProgress } from '../../api/commissionProgressApi';
import { useUser } from '../../context/userContext';
import { handlePriceWithRM } from '../../utils/priceUtils';

dayjs.extend(quarterOfYear);

const FONT = "'Poppins', sans-serif";

// Same presets, same order, All Time first and default — as on every other report.
const DATE_PRESETS = [
  { label: 'All Time', start: () => null, end: () => null },
  { label: 'This Month', start: () => dayjs().startOf('month'), end: () => dayjs() },
  { label: 'Last Month', start: () => dayjs().subtract(1, 'month').startOf('month'), end: () => dayjs().subtract(1, 'month').endOf('month') },
  { label: 'This Quarter', start: () => dayjs().startOf('quarter'), end: () => dayjs() },
  { label: 'This Year', start: () => dayjs().startOf('year'), end: () => dayjs() },
];

// Is `d` inside [start, end]? Either bound may be null (open-ended).
const inRange = (d, start, end) => {
  if (!start && !end) return true;
  if (!d) return false;
  const x = dayjs(d);
  if (start && x.isBefore(dayjs(start).startOf('day'))) return false;
  if (end && x.isAfter(dayjs(end).endOf('day'))) return false;
  return true;
};

// Agents have no state/city of their own — only a free-text Address — so an agent's
// location is taken from WHERE THEY SELL: the customer state/city on their own (PSC)
// sales. Free text arrives inconsistently ("SELANGOR", "Selangor", "Pulau Penang",
// "Pulau Pinang."), so tidy and fold the variants or the same place lists twice.
// (user 2026-08-26)
const PLACE_ALIASES = {
  'Pulau Penang': 'Pulau Pinang',
  'Penang': 'Pulau Pinang',
  'Wp Kuala Lumpur': 'Kuala Lumpur',
  'Kl': 'Kuala Lumpur',
  'N Sembilan': 'Negeri Sembilan',
};
const normPlace = (raw) => {
  const t = String(raw || '').trim().replace(/[.,;]+$/, '').replace(/\s+/g, ' ');
  if (!t) return '';
  const titled = t.toLowerCase().replace(/\b[a-z]/g, c => c.toUpperCase());
  return PLACE_ALIASES[titled] || titled;
};

const PIE_COLORS = [
  '#1A8CFF', '#F59E0B', '#10B981', '#6366F1', '#EF4444',
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#3B82F6',
];

const STATUS_COLORS = { Active: '#10B981', Archived: '#94A3B8', Inactive: '#EF4444' };

// System accounts to exclude from counts
const SYSTEM_USERNAMES = ['webbey', 'admin'];

function isSystemUser(agent) {
  const uname = (agent.username ?? '').toLowerCase();
  return SYSTEM_USERNAMES.some(s => uname.includes(s)) && !agent.groupName;
}

export default function AgentReport() {
  const { user } = useUser();
  const hasAccess = user?.roleRights?.includes('UserManagement') || user?.roleRights?.includes('AllSalesList');

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hasAccess) return;
    (async () => {
      try {
        const res = await getAllAgents();
        setAgents(res.data ?? []);
      } catch (err) {
        console.error('Error fetching agents:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [hasAccess]);

  /* ---------- Date range ----------
     This report holds two kinds of data with two different natural dates, so one
     range is applied to each by ITS OWN date rather than forcing a single field:

       agent-based  (counts, by group, by role, registrations, comm rates)
                    → registration date, i.e. who JOINED in the window
       sales-derived (Agents by State / City, Top Performer by Location)
                    → deposit date, matching every other report in the app

     Read together: "in this window, who joined and where did people sell".
     All Time is first and the default, so the page opens on the full picture.
     (user 2026-08-27) */
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activePreset, setActivePreset] = useState('All Time');
  const dated = !!(startDate || endDate);
  const handlePreset = (p) => {
    setActivePreset(p.label);
    setStartDate(p.start());
    setEndDate(p.end());
  };

  /* ---------- Filtered (exclude system users, then the date range) ---------- */
  const filtered = useMemo(() => {
    const base = agents.filter(a => !isSystemUser(a));
    if (!dated) return base;
    // An agent with no registration date can't be placed in a window, so they drop
    // out of a dated view — but never out of All Time, which is the default.
    return base.filter(a => inRange(a.createdOn, startDate, endDate));
  }, [agents, dated, startDate, endDate]);

  /* ---------- Summary metrics ---------- */
  const totalAgents = filtered.length;
  const activeAgents = useMemo(() => filtered.filter(a => a.status === 'Active').length, [filtered]);
  const archivedAgents = useMemo(() => filtered.filter(a => a.status === 'Archived').length, [filtered]);

  const hasCreatedOn = useMemo(() => filtered.some(a => a.createdOn), [filtered]);
  const newThisMonth = useMemo(() => {
    if (!hasCreatedOn) return null;
    const startOfMonth = dayjs().startOf('month');
    return filtered.filter(a => a.createdOn && dayjs(a.createdOn).isAfter(startOfMonth.subtract(1, 'day'))).length;
  }, [filtered, hasCreatedOn]);

  /* ---------- Charts data ---------- */

  // Agents by Group (pie)
  const agentsByGroup = useMemo(() => {
    const map = {};
    filtered.forEach(a => {
      const group = a.groupName || 'Unassigned';
      map[group] = (map[group] ?? 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filtered]);

  /* ---------- Agents by State / City ----------
     Where each agent actually sells, from the customer location on their OWN sales.
     PSC rows only: an agent earns DOC/TOC on a downline's sale, and counting those
     would place the agent in a state they never sold in. (user 2026-08-26) */
  const [commRows, setCommRows] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getAllCommissionProgress()
      .then(r => { if (!cancelled) setCommRows(r.data || []); })
      .catch(() => { /* no data → the two location charts simply show empty */ });
    return () => { cancelled = true; };
  }, []);

  // Sales-derived sections below run off this, filtered by DEPOSIT date so they line
  // up with the Sales and Commission reports rather than the agent-registration date
  // the cards use.
  const commRowsInRange = useMemo(() => {
    if (!dated) return commRows;
    return commRows.filter(r => inRange(r.salesDetailsForCommissionProgress?.depositOn, startDate, endDate));
  }, [commRows, dated, startDate, endDate]);

  // agentsByPlace('state'|'city') → [{ name, value }] where value = DISTINCT agents.
  // Counting rows would rank a state by how busy it is, not by how many agents work it.
  const agentsByPlace = useMemo(() => {
    const build = (field) => {
      const map = {};
      commRowsInRange.forEach(r => {
        const cd = r.commissionProgressDto;
        const sd = r.salesDetailsForCommissionProgress;
        if (!sd || !cd?.paymentTo) return;
        if (!String(cd.commPayType || '').startsWith('PSC')) return;      // own sales only
        if (sd.isArchived) return;
        if (sd.status === 'Pending' || sd.status === 'Refunded' || sd.status === 'Archived') return;
        const key = normPlace(field === 'state' ? sd.state : sd.city);
        if (!key) return;
        (map[key] ||= new Set()).add(cd.paymentTo);
      });
      return Object.entries(map)
        .map(([name, set]) => ({ name, value: set.size }))
        .sort((a, b) => b.value - a.value);
    };
    return { state: build('state'), city: build('city') };
  }, [commRowsInRange]);

  /* ---------- Top performing salesperson per location ----------
     "Agents by State" and "Agents by City" answer where the people are. This answers
     who is winning THERE — one row per "City, State", naming the agent with the most
     revenue in that place. City and state are combined into one label because neither
     alone identifies a market: there is a Kampung Baru in half the states, and a state
     row hides that Klang and Shah Alam are different patches. (user 2026-08-27)

     Revenue follows the house convention — FinalPrice + AddOnPrice — and a co-broke
     sale (PSC/2) counts half to each side, so the two co-brokers don't each get
     credited the whole deal. Own (PSC) sales only, same as the charts above. */
  const topByLocation = useMemo(() => {
    const places = {};
    commRowsInRange.forEach(r => {
      const cd = r.commissionProgressDto;
      const sd = r.salesDetailsForCommissionProgress;
      if (!sd || !cd?.paymentTo) return;
      if (!String(cd.commPayType || '').startsWith('PSC')) return;
      if (sd.isArchived) return;
      if (sd.status === 'Pending' || sd.status === 'Refunded' || sd.status === 'Archived') return;

      const city = normPlace(sd.city);
      const state = normPlace(sd.state);
      if (!city && !state) return;
      const label = [city, state].filter(Boolean).join(', ');

      const share = String(cd.commPayType).includes('/2') ? 0.5 : 1;
      const rev = ((sd.finalPrice ?? 0) + (sd.addOnPrice ?? 0)) * share;

      const p = (places[label] ||= { label, city, state, revenue: 0, saleIds: new Set(), agents: {} });
      p.revenue += rev;
      if (sd.salesId) p.saleIds.add(sd.salesId);

      const a = (p.agents[cd.paymentTo] ||= { id: cd.paymentTo, name: cd.paymentToName || 'Unknown', revenue: 0, saleIds: new Set() });
      a.revenue += rev;
      if (sd.salesId) a.saleIds.add(sd.salesId);
    });

    return Object.values(places)
      .map(p => {
        const ranked = Object.values(p.agents).sort((x, y) => y.revenue - x.revenue);
        const top = ranked[0];
        return {
          label: p.label,
          revenue: p.revenue,
          sales: p.saleIds.size,
          agentCount: ranked.length,
          topName: top?.name || '—',
          topRevenue: top?.revenue || 0,
          topSales: top?.saleIds.size || 0,
          // How dominant the leader is. A 100% share means they are the only agent
          // selling there, which reads very differently from leading a crowded market.
          topShare: p.revenue > 0 ? Math.round((top?.revenue || 0) / p.revenue * 100) : 0,
        };
      })
      .sort((a, b) => b.revenue - a.revenue);
  }, [commRowsInRange]);

  // Agents by Role (bar horizontal)
  const agentsByRole = useMemo(() => {
    const map = {};
    filtered.forEach(a => {
      (a.userRoles ?? []).forEach(r => {
        const role = r.roleName ?? 'Unknown';
        map[role] = (map[role] ?? 0) + 1;
      });
    });
    return Object.entries(map).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count);
  }, [filtered]);

  // Status breakdown
  const statusData = useMemo(() => {
    const map = {};
    filtered.forEach(a => {
      const st = a.status || 'Unknown';
      map[st] = (map[st] ?? 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  }, [filtered]);

  // New registrations by month (last 12 months)
  const registrationsByMonth = useMemo(() => {
    if (!hasCreatedOn) return [];
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const m = dayjs().subtract(i, 'month');
      months.push({ key: m.format('YYYY-MM'), label: m.format('MMM YYYY'), count: 0 });
    }
    filtered.forEach(a => {
      if (!a.createdOn) return;
      const key = dayjs(a.createdOn).format('YYYY-MM');
      const bucket = months.find(m => m.key === key);
      if (bucket) bucket.count += 1;
    });
    return months.map(({ label, count }) => ({ name: label, count }));
  }, [filtered, hasCreatedOn]);

  // Commission rate distribution (PSC)
  const commissionDist = useMemo(() => {
    const map = {};
    filtered.forEach(a => {
      const rate = a.personalComm ?? 0;
      const label = `${rate}%`;
      map[label] = (map[label] ?? 0) + 1;
    });
    return Object.entries(map)
      .map(([name, count]) => ({ name, count, sortKey: parseFloat(name) }))
      .sort((a, b) => a.sortKey - b.sortKey);
  }, [filtered]);

  // Distinct agents who actually closed a sale in the window. Only meaningful with a
  // range on, where it pairs the two halves of the page: who joined vs who sold.
  const agentsWhoSold = useMemo(() => {
    const ids = new Set();
    commRowsInRange.forEach(r => {
      const cd = r.commissionProgressDto;
      const sd = r.salesDetailsForCommissionProgress;
      if (!sd || !cd?.paymentTo) return;
      if (!String(cd.commPayType || '').startsWith('PSC')) return;
      if (sd.isArchived) return;
      if (sd.status === 'Pending' || sd.status === 'Refunded' || sd.status === 'Archived') return;
      ids.add(cd.paymentTo);
    });
    return ids.size;
  }, [commRowsInRange]);

  const rangeLabel = useMemo(() => {
    if (!dated) return '';
    const f = (d) => dayjs(d).format('DD/MM/YYYY');
    if (startDate && endDate) return `${f(startDate)} – ${f(endDate)}`;
    return startDate ? `From ${f(startDate)}` : `Up to ${f(endDate)}`;
  }, [dated, startDate, endDate]);

  /* ---------- Summary cards config ----------
     The labels change with the range because the numbers do. With no range the
     roster is a snapshot ("Total Agents"); with a range it's everyone who JOINED
     in that window, and calling that "Total Agents" would read as the whole
     workforce. The fourth card swaps to who actually sold in the window, which is
     the sales-side counterpart. (user 2026-08-27) */
  const summaryCards = dated ? [
    { label: 'Agents Joined', value: totalAgents, sub: rangeLabel, borderColor: '#1A8CFF' },
    { label: 'Active', value: activeAgents, sub: 'Of those who joined', borderColor: '#10B981' },
    { label: 'Archived', value: archivedAgents, sub: 'Of those who joined', borderColor: '#94A3B8' },
    { label: 'Agents Who Sold', value: agentsWhoSold, sub: 'Closed a sale in range', borderColor: '#F59E0B' },
  ] : [
    { label: 'Total Agents', value: totalAgents, sub: 'All registered agents', borderColor: '#1A8CFF' },
    { label: 'Active Agents', value: activeAgents, sub: 'Currently active', borderColor: '#10B981' },
    { label: 'Archived', value: archivedAgents, sub: 'Archived accounts', borderColor: '#94A3B8' },
    { label: 'New This Month', value: newThisMonth !== null ? newThisMonth : 'N/A', sub: newThisMonth !== null ? dayjs().format('MMMM YYYY') : 'Date not available', borderColor: '#F59E0B' },
  ];

  const datePickerSx = { width: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: FONT, fontSize: '0.85rem' } };

  /* ---------- Render ---------- */
  if (!hasAccess) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: FONT, color: '#64748B' }}>You do not have permission to view this report.</Typography>
      </Box>
    );
  }

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      {/* ===== Header ===== */}
      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: FONT }}>Agent Report</h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: FONT }}>
            Agent workforce overview and statistics
            {dated && ' · agents by registration date, sales by deposit date'}
          </p>
        </div>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <DatePicker value={startDate} onChange={v => v && (setStartDate(v), setActivePreset(null))} format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: datePickerSx } }} />
          <Typography sx={{ color: '#64748B', fontSize: '0.85rem' }}>to</Typography>
          <DatePicker value={endDate} onChange={v => v && (setEndDate(v), setActivePreset(null))} format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: datePickerSx } }} />
        </Box>
      </Box>

      {/* Date presets */}
      <Box className="no-print" sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
        {DATE_PRESETS.map(p => (
          <Chip key={p.label} label={p.label} size="small" onClick={() => handlePreset(p)}
            sx={{
              fontFamily: FONT, fontSize: '0.75rem',
              fontWeight: activePreset === p.label ? 600 : 400,
              backgroundColor: activePreset === p.label ? '#1A8CFF' : '#F1F5F9',
              color: activePreset === p.label ? '#fff' : '#475569',
              '&:hover': { backgroundColor: activePreset === p.label ? '#1574D4' : '#E2E8F0' },
            }} />
        ))}
      </Box>

      {/* ===== Summary Cards ===== */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        {summaryCards.map(card => (
          <Card key={card.label} sx={{ borderLeft: `4px solid ${card.borderColor}`, borderRadius: '14px' }}>
            <CardContent sx={{ p: '14px !important', '&:last-child': { pb: '14px !important' } }}>
              <Typography sx={{ fontSize: '0.7rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', fontFamily: FONT }}>
                {card.label}
              </Typography>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#0F172A', mt: 0.5, fontFamily: FONT }}>
                {card.value}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mt: 0.25, fontFamily: FONT }}>
                {card.sub}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* ===== Charts Row: Group + Role ===== */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
        {/* Agents by Group (Donut) */}
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>
              Agents by Group
            </Typography>
            {agentsByGroup.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={agentsByGroup}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={false}
                  >
                    {agentsByGroup.map((entry, i) => (
                      <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend formatter={value => (
                    <span style={{ fontSize: '0.8rem', fontFamily: FONT, color: '#374151' }}>{value}</span>
                  )} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No group data available</Typography>
            )}
          </CardContent>
        </Card>

        {/* Agents by Role (Horizontal Bar) */}
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>
              Agents by Role
            </Typography>
            {agentsByRole.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(200, agentsByRole.length * 45)}>
                <BarChart data={agentsByRole} layout="vertical" margin={{ left: 20, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" allowDecimals={false} style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <YAxis type="category" dataKey="name" width={120} style={{ fontSize: '0.75rem', fontFamily: FONT }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#6366F1" radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No role data available</Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* ===== Agent Status Breakdown ===== */}
      <Box sx={{ mb: 2 }}>
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>
              Agent Status Breakdown
            </Typography>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={75}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={false}
                  >
                    {statusData.map(entry => (
                      <Cell key={entry.name} fill={STATUS_COLORS[entry.name] ?? '#94A3B8'} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend formatter={value => (
                    <span style={{ fontSize: '0.8rem', fontFamily: FONT, color: '#374151' }}>{value}</span>
                  )} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No status data</Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* ===== Agents by State / Agents by City =====
           Location comes from where each agent SELLS (customer state/city on their own
           PSC sales), because agents carry no state/city field themselves. Value is
           DISTINCT AGENTS, not sales — this answers "where are my people", not "where is
           the money". Same horizontal-bar treatment as the Sales Report. (user 2026-08-26) */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
        {[
          { key: 'state', title: 'Agents by State', data: agentsByPlace.state, colour: '#1A8CFF', noun: 'states' },
          { key: 'city',  title: 'Agents by City',  data: agentsByPlace.city,  colour: '#10B981', noun: 'cities' },
        ].map(({ key, title, data, colour, noun }) => (
          <Card key={key} sx={{ borderRadius: '14px' }}>
            <CardContent>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 0.5, fontFamily: FONT }}>{title}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mb: 2, fontFamily: FONT }}>
                {data.length} {noun} · agents with own sales there{key === 'city' ? ' · top 15' : ''}
              </Typography>
              {data.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(220, Math.min(data.length, 15) * 30)}>
                  <BarChart data={data.slice(0, 15)} layout="vertical" margin={{ left: 10, right: 40, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#EEF2F6" />
                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fontFamily: FONT, fill: '#64748B' }} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fontFamily: FONT, fill: '#334155' }} />
                    <Tooltip formatter={(v) => [`${v} agent${v === 1 ? '' : 's'}`, 'Agents']}
                      contentStyle={{ fontFamily: FONT, fontSize: '0.78rem', borderRadius: 8 }} />
                    <Bar dataKey="value" fill={colour} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No location data</Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* ===== Top Performing Salesperson by Location ===== */}
      <Box sx={{ mb: 2 }}>
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 0.5, fontFamily: FONT }}>
              Top Performing Salesperson by Location
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748B', mb: 2, fontFamily: FONT }}>
              {topByLocation.length} locations (city, state) · own sales · co-broke counted as half · sorted by location revenue
            </Typography>
            {topByLocation.length > 0 ? (
              <TableContainer sx={{ maxHeight: 520, overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {['#', 'Location', 'Top Salesperson', 'Their Sales', 'Their Revenue', 'Share', 'Location Revenue', 'Agents'].map((h, i) => (
                        <TableCell key={h}
                          align={i >= 3 && i !== 7 ? 'right' : i === 7 ? 'right' : 'left'}
                          sx={{ fontFamily: FONT, fontSize: '0.72rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '2px solid #E2E8F0', backgroundColor: '#FFF' }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topByLocation.map((p, i) => (
                      <TableRow key={p.label} hover>
                        <TableCell sx={{ fontFamily: FONT, fontSize: '0.8rem', color: '#64748B', fontWeight: 600, borderBottom: '1px solid #F1F5F9' }}>{i + 1}</TableCell>
                        <TableCell sx={{ fontFamily: FONT, fontSize: '0.8rem', color: '#0F172A', borderBottom: '1px solid #F1F5F9' }}>{p.label}</TableCell>
                        <TableCell sx={{ fontFamily: FONT, fontSize: '0.8rem', color: '#0F172A', fontWeight: 600, borderBottom: '1px solid #F1F5F9' }}>{p.topName}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: FONT, fontSize: '0.8rem', color: '#0F172A', borderBottom: '1px solid #F1F5F9' }}>{p.topSales}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: FONT, fontSize: '0.8rem', color: '#0F172A', fontWeight: 700, borderBottom: '1px solid #F1F5F9' }}>{handlePriceWithRM(Math.round(p.topRevenue))}</TableCell>
                        <TableCell align="right" sx={{ borderBottom: '1px solid #F1F5F9' }}>
                          <Chip size="small" label={`${p.topShare}%`}
                            sx={{ fontFamily: FONT, fontSize: '0.68rem', height: 20,
                                  backgroundColor: p.topShare >= 75 ? '#DCFCE7' : p.topShare >= 40 ? '#FEF3C7' : '#E2E8F0',
                                  color: p.topShare >= 75 ? '#166534' : p.topShare >= 40 ? '#92400E' : '#475569' }} />
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: FONT, fontSize: '0.8rem', color: '#334155', borderBottom: '1px solid #F1F5F9' }}>{handlePriceWithRM(Math.round(p.revenue))}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: FONT, fontSize: '0.8rem', color: '#64748B', borderBottom: '1px solid #F1F5F9' }}>{p.agentCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4, fontFamily: FONT }}>No location data</Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* ===== New Agent Registrations by Month ===== */}
      <Box sx={{ mb: 2 }}>
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>
              New Agent Registrations by Month
            </Typography>
            {!hasCreatedOn ? (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4, fontFamily: FONT }}>
                Registration date not available
              </Typography>
            ) : registrationsByMonth.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={registrationsByMonth} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <YAxis allowDecimals={false} style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1A8CFF" radius={[4, 4, 0, 0]} barSize={32} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No registration data</Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* ===== Commission Rate Distribution ===== */}
      <Box sx={{ mb: 2 }}>
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0F172A', mb: 2, fontFamily: FONT }}>
              Commission Rate Distribution (PSC)
            </Typography>
            {commissionDist.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={commissionDist} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <YAxis allowDecimals={false} style={{ fontSize: '0.7rem', fontFamily: FONT }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#F59E0B" radius={[4, 4, 0, 0]} barSize={40} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography sx={{ fontSize: '0.85rem', color: '#64748B', textAlign: 'center', py: 4 }}>No commission data</Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </div>
  );
}
